import { z } from "zod";
import { createFileRoute } from "@tanstack/react-router";
import { TablePagination, useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";

import ListLayout from "../../../components/Layout/ListLayout";

import { useAuth } from "../../../auth";
import { useListTasks } from "../../../api/bpm/endpoints";
import { CustomTaskBpmDto, ListTasksParams } from "../../../api/bpm/model";
import TaskList from "../../../components/Tasks/TaskList";
import TaskCards from "../../../components/Tasks/TaskCards";
import TaskFilters, {
  TaskFilterValues,
} from "../../../components/Tasks/TaskFilters";
import { encodeNavParams } from "../../../utils/navParams";
import { toLocalDateBoundary } from "../../../components/Processes/processQueryParams";

const tasksSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
  situation: z
    .enum(["", "ATIVA", "INATIVA", "ATRASADA", "NO_PRAZO"])
    .optional(),
  dueDateFrom: z.string().optional(),
  dueDateTo: z.string().optional(),
  createTimeFrom: z.string().optional(),
  createTimeTo: z.string().optional(),
  withoutDueDate: z.boolean().optional(),
  orderBy: z.string().catch("dueDate"),
  order: z.enum(["asc", "desc"]).catch("asc"),
});

export const Route = createFileRoute("/__auth/tasks/")({
  component: TasksPage,
  validateSearch: (search) => tasksSearchSchema.parse(search),
  staticData: {
    title: "routes.myTasks",
  },
});

interface TaskPage {
  content?: CustomTaskBpmDto[];
  totalElements?: number;
  totalPages?: number;
  size?: number;
  number?: number;
}

function TasksPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();

  const navigate = Route.useNavigate();
  const { t } = useTranslation();
  const search = Route.useSearch();

  const {
    data: responseData,
    isLoading,
    error,
  } = useListTasks(
    {
      isPageable: true,
      page: (search.page || 1) - 1,
      size: search.size || 10,
      sort: `${search.orderBy || "dueDate"},${search.order || "asc"}`,
      searchBy: "MY_TASK",
      assignee: user?.id || undefined,
      q: search.q || undefined,
      situation: search.situation || undefined,
      dueDateFrom: search.dueDateFrom
        ? toLocalDateBoundary(search.dueDateFrom, false)
        : undefined,
      dueDateTo: search.dueDateTo ? toLocalDateBoundary(search.dueDateTo, true) : undefined,
      createTimeFrom: search.createTimeFrom
        ? toLocalDateBoundary(search.createTimeFrom, false)
        : undefined,
      createTimeTo: search.createTimeTo
        ? toLocalDateBoundary(search.createTimeTo, true)
        : undefined,
      withoutDueDate: search.withoutDueDate || undefined,
    } as unknown as ListTasksParams,
    {
      query: {
        enabled: !!user?.id,
      },
    },
  );

  const data = responseData as unknown as TaskPage;

  const filteredTasks = (data?.content || []).filter((task) => {
    if (
      search.situation &&
      task.situation &&
      search.situation !== task.situation
    ) {
      return false;
    }
    return true;
  });

  const handlePageChange = (_: unknown, value: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: value }),
      replace: true,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTaskClick = (task: any) => {
    const fromParams = encodeNavParams({
      page: search.page,
      size: search.size,
      orderBy: search.orderBy,
      order: search.order,
      q: search.q,
      situation: search.situation,
      dueDateFrom: search.dueDateFrom,
      dueDateTo: search.dueDateTo,
      createTimeFrom: search.createTimeFrom,
      createTimeTo: search.createTimeTo,
      withoutDueDate: search.withoutDueDate,
    });
    navigate({
      to: "/tasks/$taskId/execute",
      params: { taskId: task.id },
      search: (() => ({ from: "tasks" as const, fromParams })) as any,
    });
  };

  const handleHistoryClick = (task: any) => {
    const fromParams = encodeNavParams({
      page: search.page,
      size: search.size,
      orderBy: search.orderBy,
      order: search.order,
      q: search.q,
      situation: search.situation,
      dueDateFrom: search.dueDateFrom,
      dueDateTo: search.dueDateTo,
      createTimeFrom: search.createTimeFrom,
      createTimeTo: search.createTimeTo,
      withoutDueDate: search.withoutDueDate,
    });
    navigate({
      to: "/processes/$processInstanceId/history/details",
      params: { processInstanceId: task.processInstanceId },
      search: (() => ({ from: "tasks" as const, fromParams })) as any,
    });
  };

  const handleFilter = (newFilters: TaskFilterValues) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...newFilters,
        page: 1,
      }),
    });
  };

  const handleRequestSort = (property: string) => {
    const isAsc = search.orderBy === property && search.order === "asc";
    navigate({
      search: (prev) => ({
        ...prev,
        orderBy: property,
        order: isAsc ? "desc" : "asc",
      }),
    });
  };

  return (
    <ListLayout
      title={t("tasks.myTasks")}
      subtitle={t("tasks.subtitle")}
      filters={
        <TaskFilters
          onFilter={handleFilter}
          isLoading={isLoading}
          initialValues={{
            q: search.q,
            situation: search.situation,
            dueDateFrom: search.dueDateFrom,
            dueDateTo: search.dueDateTo,
            createTimeFrom: search.createTimeFrom,
            createTimeTo: search.createTimeTo,
            withoutDueDate: search.withoutDueDate,
          }}
        />
      }
      error={error}
      isLoading={isLoading}
      isEmpty={!filteredTasks.length}
      emptyMessage={t("tasks.emptyMessage")}
    >
      {isMobile ? (
        <TaskCards
          tasks={filteredTasks}
          onTaskClick={handleTaskClick}
          onHistoryClick={handleHistoryClick}
        />
      ) : (
        <TaskList
          tasks={filteredTasks}
          onTaskClick={handleTaskClick}
          onHistoryClick={handleHistoryClick}
          orderBy={search.orderBy || "dueDate"}
          order={search.order || "asc"}
          onRequestSort={handleRequestSort}
        />
      )}

      <TablePagination
        component="div"
        count={data?.totalElements || 0}
        page={(search.page || 1) - 1}
        onPageChange={(_, newPage) => handlePageChange(null, newPage + 1)}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={(e) => {
          const newSize = parseInt(e.target.value, 10);
          navigate({
            search: (prev) => ({ ...prev, size: newSize, page: 1 }),
            replace: true,
          });
        }}
        labelRowsPerPage={t("common.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count}`
        }
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          mb: 8,
        }}
      />
    </ListLayout>
  );
}
